import React, {PureComponent} from 'react';
import styled from 'styled-components';

import Input from '../../components/Input';
import JsonLogicBuilder from '../../components/JsonLogicBuilder';
import {getLogicBuilderVariables} from './shared';
import Button from '../../components/Button';
import SubConfiguration from './SubConfiguration';
import DeletableCard from './DeletableCard';
import ChipInput from '../../components/ChipInput';
import {v4 as uuid} from 'uuid';

class NotificationBuilder extends PureComponent {

  handleRemove = () => {
    const {notification, onRemove} = this.props;
    onRemove(notification);
  }

  setNotificationState = reducer => {
    const {notification, onChange} = this.props;

    onChange({
      ...notification,
      ...(typeof reducer === 'function' ? reducer(notification) : reducer)
    });
  }

  handleChangeType = e => {
    const {notification, users} = this.props;
    const newType = e.target.value;

    if (notification.type === newType) return;

    const wasSheetComplete = notification.type === "sheetCompleteContacts";
    const willBeSheetComplete = newType === "sheetCompleteContacts";

    let normalizedUsers = notification.users || [];
    let stashedExpressions = notification._stashedExpressions || [];

    if (wasSheetComplete !== willBeSheetComplete) {
      if (willBeSheetComplete) {
        // From expression-based to simple users: stash expressions, extract user objects
        stashedExpressions = normalizedUsers;
        normalizedUsers = normalizedUsers
          .map(entry => entry.user || users.find(u => u.id === entry.userId))
          .filter(Boolean)
          .map(user => {
            // Ensure toString method exists for ChipInput display
            if (!user.toString || user.toString === Object.prototype.toString) {
              user.toString = () => user.userName;
            }
            return user;
          });
      } else {
        // From simple users to expression-based: restore stashed expressions where possible
        const stashedByUserId = {};
        stashedExpressions.forEach(entry => {
          const id = entry.userId || (entry.user && entry.user.id);
          if (id) stashedByUserId[id] = entry;
        });

        normalizedUsers = normalizedUsers.map(user => {
          const stashed = stashedByUserId[user.id];
          if (stashed) {
            delete stashedByUserId[user.id];
            return stashed;
          }
          return {userId: user.id, expression: null, tempId: uuid(), user};
        });
        stashedExpressions = [];
      }
    }

    this.setNotificationState({
      type: newType,
      users: normalizedUsers,
      _stashedExpressions: stashedExpressions
    });
  }

  handleChangeUsers = (selectedUsers) => {
    this.setNotificationState({users: selectedUsers})
  }

  handleExpressionChanged = (expression, tempId) => {
    this.setNotificationState(notification => ({
      users: notification.users.map(u=>
          u.tempId === tempId ? {...u, expression: JSON.stringify(expression)}: u
        )
    }));
  }

  handleChangeUserRecipient = (tempId, user) => {
    // Takes in user and tempId from the notification.users list
    if(user===null){return}
    this.setNotificationState((notification)=>{ // map the user to the userExpression with the same tempId (uuid)
      return {...notification,
        users: notification.users.map(expr => expr.tempId === tempId
            ? {...expr, userId: user.id, user:user}
            : expr)
      }
    })
  }

  handleAddRecipient = () => {
    this.setNotificationState((notification)=>{
      const updated =[
        ...(notification.users || []),
        {userId:null, expression:null, tempId: uuid()} //creates new empty userExpression
        ];
        return {users: updated}
    });
  }

  handleRemoveRecipient = (tempId) => {
    this.setNotificationState((notification)=> {
      const updatedUsers = notification.users.filter(u => u.tempId !== tempId)
      //filter out one that is being removed based on uuid
      return {users: updatedUsers}
    })
  }

  handleToggleCollapsed = () => {
    this.setNotificationState((notification)=>{
      const updatedCollapsed = !notification.collapsed //toggle collapse
      return {collapsed: updatedCollapsed}
    })
  }

  formatTypeLabel(type) { //Format the options to text (as before)
    const map = {
      sheetCompleteContacts: "A sheet is completed",
      rowCompleteContacts: "All of the questions are answered",
      rowApprovedContacts: "A submission is approved",
      rowRejectedContacts: "A submission is rejected",
    };
    return map[type] || type;
  }

  render() {
    const {notification, notifications, users, reviewersEnabled, form, catalog} = this.props;
    const collapsed = notification.collapsed || false
    const allTypes = [
      "sheetCompleteContacts",
      "rowCompleteContacts",
      ...(reviewersEnabled
        ? ["rowApprovedContacts", "rowRejectedContacts"]
        : [])
    ]; // list of all types that can be used for notifications 
    const selectedTypes = notifications.map(n => n.type); //notification types that are being used 
    const currentType = notification.type; //current notification that is being rendered 

    const availableTypes = allTypes.filter(type =>
      type === currentType || !selectedTypes.includes(type)
    ); //check for types that are not being used and give the options to select them later

    return (
      <DeletableCard
        focusable
        dialogTitle="Delete this notification?"
        dialogDescription="Are you sure you want to delete this notification? This cannot be undone."
        ok="Delete"
        onOk={this.handleRemove}>
        <NotificationForm>
          <Input
            id={`notification_${notification.id}_trigger`}
            label="Send a Notification When..."
            type="select"
            disabled={notifications.length === (reviewersEnabled ? 4 : 2)}
            value={notification.type}
            onChange={this.handleChangeType}>
            {availableTypes.map(type => (
            <option key={type} value={type}>
              {this.formatTypeLabel(type)}
            </option>
          ))}
          </Input>
          {notification.type === "sheetCompleteContacts" && (
            <ChipInput
            id={`notification_${notification.id}_contacts`}
            label="Notification contacts"
            placeholder=""
            value={notification.users}
            suggestions={users}
            onChange={this.handleChangeUsers}
          />
          )}
          {notification.type !== "sheetCompleteContacts" && (
            <>
            {(collapsed
                    ? (notification.users || []).slice(0, 3) // if collapsed is true show the first 3
                    : (notification.users || [])) // otherwise show all notification expressions
                .sort((a, b) => {
                  const nameA = a.user && a.user.userName ? a.user.userName : '';
                  const nameB = b.user && b.user.userName ? b.user.userName : '';

                  if (!nameA && !nameB) return 0; // tie both empty
                  if (!nameA) return 1;  //nameA empty (goes on bottom)
                  if (!nameB) return -1; //nameB empty (goes on bottom)
                  return nameA.localeCompare(nameB)
                }) //sort alphabetically through users whenever screen is rendered
                .map((userExpr) => {
              const selectedUserIds = (notification.users || [])
                .map(u => u.userId || (u.user && u.user.id))
                .filter(id => id && id !== (userExpr.userId || (userExpr.user && userExpr.user.id)));
              const availUsers = users.filter(u => !selectedUserIds.includes(u.id));
              return (
                <RecipientSubForm
                  key={userExpr.tempId}
                  notification={notification}
                  userExpr={userExpr}
                  users={availUsers}
                  form={form}
                  catalog={catalog}
                  handleChangeUserRecipient={this.handleChangeUserRecipient}
                  handleExpressionChanged={this.handleExpressionChanged}
                  handleRemoveRecipient={this.handleRemoveRecipient}
                />
              );
            })}
            <ButtonContainer>
              <Button
                secondary
                onClick={()=> this.handleAddRecipient()}
                disabled ={(notification.users ||[]).length>=users.length} //disable if all users are accounted for
              > Add Recipient
              </Button>
              {notification.users.length > 3 && <Button
                  secondary
                  onClick={this.handleToggleCollapsed}> {collapsed ? "Show More" : "Show Less"}
              </Button>
              }
            </ButtonContainer>
          </>
          )}
        </NotificationForm>
      </DeletableCard>
    );
  }
}

const RecipientSubForm = ({
    userExpr,
    users,
    notification,
    handleChangeUserRecipient,
    handleExpressionChanged,
    handleRemoveRecipient,
    form,
    catalog
  }) => {
    const user = userExpr.user || users.find(u => u.id === userExpr.userId);
    const userId = user ? user.id : null;
    const disableSelect = users.length === 0 || (users.length === 1 && userId && users[0].id === userId);
    //checks to see if there are any users left or if the only one left is selected
    return (
      <DeletableCard
          focusable
          key={userExpr.tempId}
          dialogTitle="Delete this notification expression?"
          dialogDescription="Are you sure you want to delete this notification expression? This cannot be undone."
          ok="Delete"
          onOk={() => handleRemoveRecipient(userExpr.tempId)}
      >
        <NotificationForm>
          <TopContainer>
              <Input
                id={`notification_${notification.id}_user_${userExpr.tempId}`}
                label = "Select User"
                type="select"
                disabled={disableSelect}
                value={user ? user.id : ""}
                onChange={(e) => {
                  const selectedUser = users.find(u => u.id === e.target.value) || null;
                  handleChangeUserRecipient(userExpr.tempId, selectedUser);
                }}
                style={{ marginTop: "4px"}}
              >
                <option value="">-- Choose a User --</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.userName}
                  </option>
                ))}
              </Input>
          </TopContainer>
          <Condition>
            <label>Trigger Notification When...</label>
            <SubConfiguration>
              <SubConfiguration.Item>
                  <JsonLogicBuilder
                    expand
                    variables={getLogicBuilderVariables(form, catalog)}
                    value={JSON.parse(userExpr.expression || "null")}
                    onChange={(expr) => handleExpressionChanged(expr, userExpr.tempId)}
                    parentType = "Notification"
                  />
                </SubConfiguration.Item>
            </SubConfiguration>
          </Condition>
        </NotificationForm>
      </DeletableCard>
    );
  };

const NotificationForm = styled.div`
  > * {
    margin-bottom: 1.5rem;

    :last-child {
      margin-bottom: 0;
    }
  }
`;

const Condition = styled.div`
  label {
    display: block;
    margin-bottom: 0.5rem;
  }
`;

const TopContainer = styled.div`
  display: flex;
  justify-content: space-between;

  > :first-child {
    flex: 3; // Gives more space to the first child so the name input extends
  }
`;

const ButtonContainer = styled.div`
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
`;
export default NotificationBuilder;
